import { customerApiClient, customerNodeClient } from "./customerHttpClient";
import { firstPublicUserId, getStoredCustomerPublicId, isPublicUserId } from "../auth/customerIdentity";

const nodeClient = customerNodeClient;

function withoutPrivateUserIds(payload = {}) {
  if (!payload || typeof payload !== "object") {
    return {};
  }
  const {
    user_id,
    viewer_id,
    follower_user_id,
    sender_user_id,
    payer_user_id,
    from_user_id,
    ...rest
  } = payload;
  return rest;
}

export async function fetchUploadRules() {
  const { data } = await nodeClient.get("/social/upload-rules");
  return data;
}

export async function uploadSocialMedia(formData, onUploadProgress) {
  if (formData && typeof formData.delete === "function") {
    formData.delete("user_id");
  }
  const { data } = await nodeClient.post("/social/upload-media", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });
  return data;
}

export async function createSocialPost(payload) {
  const { data } = await nodeClient.post("/social/create-post", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchUploadStatus(params) {
  const { data } = await nodeClient.get("/social/upload-status", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function reactSocialPost(payload) {
  const { data } = await nodeClient.post("/social/react", withoutPrivateUserIds(payload));
  return data;
}

export async function shareSocialPost(payload) {
  const { data } = await nodeClient.post("/social/share", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchPendingFollowRequests(payload) {
  const { data } = await nodeClient.post("/follow/pendingRequests", withoutPrivateUserIds(payload));
  return data;
}

export const getPendingFollowRequests = fetchPendingFollowRequests;

export async function followUserRequest(payload) {
  const { data } = await nodeClient.post("/follow/followUser", withoutPrivateUserIds(payload));
  return data;
}

export async function unfollowUserRequest(payload) {
  const { data } = await nodeClient.post("/follow/unfollow", withoutPrivateUserIds(payload));
  return data;
}

export async function acceptFollowRequest(payload) {
  const { data } = await nodeClient.post("/follow/acceptRequest", payload);
  return data;
}

export async function rejectFollowRequest(payload) {
  const { data } = await nodeClient.post("/follow/rejectRequest", payload);
  return data;
}

export async function updateSocialPrivacy(payload) {
  const { data } = await nodeClient.post("/social/privacy/update", withoutPrivateUserIds(payload));
  return data;
}

export async function cancelFollowRequest(payload) {
  const { data } = await nodeClient.post("/follow/cancelRequest", withoutPrivateUserIds(payload));
  return data;
}

export async function deleteSocialPost(payload) {
  const { data } = await nodeClient.post("/social/delete-post", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchSocialNotifications(params) {
  const { data } = await nodeClient.get("/social/notifications", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function markSocialNotificationRead(payload) {
  const { data } = await nodeClient.post("/social/notifications/mark-read", withoutPrivateUserIds(payload));
  return data;
}

export async function markAllSocialNotificationsRead(payload) {
  const { data } = await nodeClient.post("/social/notifications/mark-all-read", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchMateOutNotifications(params) {
  const { data } = await nodeClient.get("/mateout/getInviteNotifications", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function fetchPushNotifications(payload = { category: "general" }) {
  const formData = new FormData();
  Object.entries(withoutPrivateUserIds({ category: "general", ...payload })).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });
  const { data } = await customerApiClient.post("/APIs/Common/pushNotifications", formData);
  return data;
}

export async function fetchPushNotificationUnreadCount(payload = { category: "general" }) {
  const formData = new FormData();
  Object.entries(withoutPrivateUserIds({ category: "general", ...payload })).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });
  const { data } = await customerApiClient.post("/APIs/Common/notificationUnreadCount", formData);
  return data;
}

export async function markAllPushNotificationsRead(payload = { category: "general" }) {
  const formData = new FormData();
  Object.entries(withoutPrivateUserIds({ category: "general", ...payload })).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });
  const { data } = await customerApiClient.post("/APIs/Common/markAllNotificationsRead", formData);
  return data;
}

export async function fetchMySocialPosts(params) {
  const { data } = await nodeClient.get("/social/my-posts", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function fetchProfileSocialData(profileTarget) {
  const publicId = firstPublicUserId(profileTarget);
  const currentPublicId = getStoredCustomerPublicId();
  const endpoint = !publicId || publicId === currentPublicId
    ? "/social/profile/me"
    : `/social/profile/${encodeURIComponent(publicId)}`;
  const { data } = await nodeClient.get(endpoint);
  return data;
}

export async function fetchMatesStatus(userA, userB) {
  const publicId = firstPublicUserId(userB);
  if (!isPublicUserId(publicId)) {
    return { status: false, data: { is_mate: false, is_requested: false } };
  }
  const { data } = await nodeClient.get(`/social/mates/me/${encodeURIComponent(publicId)}`);
  return data;
}

export async function fetchMatesList(payload) {
  const { data } = await nodeClient.post("/follow/mates", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchFollowedVenues(payload) {
  const { data } = await nodeClient.post("/follow/followed-venues", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchReferralLink(params) {
  const { data } = await nodeClient.get("/social/referral-link", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function syncSocialContacts(payload) {
  const { data } = await nodeClient.post("/social/contacts/sync", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchSocialContacts(params) {
  const { data } = await nodeClient.get("/social/contacts", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function inviteSocialContact(payload) {
  const { data } = await nodeClient.post("/social/invite", withoutPrivateUserIds(payload));
  return data;
}

export async function fetchSocialInvites(params) {
  const { data } = await nodeClient.get("/social/invites", { params: withoutPrivateUserIds(params) });
  return data;
}

export async function markPushNotificationRead(notificationId) {
  const formData = new FormData();
  formData.append("notification_id", notificationId);
  const { data } = await customerApiClient.post("/APIs/Common/readNotification", formData);
  return data;
}

export async function updateUserBio(userId, bio) {
  const formData = new FormData();
  formData.append("bio", bio || "");
  const { data } = await customerApiClient.post("/api/UserAuth/updateBio", formData);
  return data;
}

export async function updateUserProfileDetails(userId, payload = {}) {
  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  const { data } = await customerApiClient.post("/Api/UserAuth/UpdateProfile", formData);
  return data;
}

export async function sendProfileContactOtp({ userId, type, target }) {
  const verificationType = type === "email" ? "email" : "mobile";
  const formData = new FormData();
  formData.append("type", verificationType);
  formData.append("verification_type", `profile_${verificationType}`);
  formData.append(verificationType === "email" ? "email" : "mobile", target);
  formData.append("otpType", verificationType === "email" ? "email" : "text");
  if (verificationType === "email" && typeof window !== "undefined") {
    formData.append("return_url", `${window.location.origin}/profile/edit`);
  }

  if (verificationType === "mobile") {
    const { data } = await customerApiClient.post("/Api/UserAuth/Login", formData);
    return data;
  }

  const { data } = await customerApiClient.post("/Api/UserAuth/sendEmailOtp", formData);
  return data;
}

export async function verifyProfileContactOtp({ userId, type, target, otp }) {
  const verificationType = type === "email" ? "email" : "mobile";
  const formData = new FormData();
  formData.append("type", verificationType);
  formData.append("verification_type", `profile_${verificationType}`);
  formData.append(verificationType === "email" ? "email" : "mobile", target);
  formData.append("otp", otp);

  if (verificationType === "mobile") {
    const { data } = await customerApiClient.post("/Api/UserAuth/verifyOtp", formData);
    return data;
  }

  const { data } = await customerApiClient.post("/Api/UserAuth/verifyEmailOtp", formData);
  return data;
}

export async function updateProfileImageV2(userId, file) {
  const formData = new FormData();
  formData.append("img", file);
  const { data } = await customerApiClient.post("/api/UserAuth/updateProfileImageV2", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function updateProfileCoverImage(userId, file, payload = {}) {
  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && !(value instanceof File)) {
      formData.append(key, value);
    }
  });
  [
    "cover_pic",
    "cover_image",
    "cover_img",
    "cover",
    "cover_photo",
    "banner_image",
  ].forEach((fieldName) => formData.append(fieldName, file));
  const { data } = await customerApiClient.post("/Api/UserAuth/UpdateProfile", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function fetchPublicProfile(userId, viewerId) {
  const formData = new FormData();
  const publicId = firstPublicUserId(userId);
  if (publicId) {
    formData.append("public_id", publicId);
  }
  const { data } = await customerApiClient.post("/api/UserAuth/getPublicProfile", formData);
  return data;
}
